import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  FormHelperText,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  IPolicyVehicle,
  IPolicyDetailsHeader,
} from "../../models/user/policyDetails/PolicyDetails";
import { useAppDispatch } from "../../store/hooks";
import { GetPolicyRisks } from "../../store/user/policyDetails/policyDetailsActions";
import { getCookie } from "../../utils/Utility";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HealingOutlinedIcon from "@mui/icons-material/HealingOutlined";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import AccessibilityOutlinedIcon from "@mui/icons-material/AccessibilityOutlined";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IncidentDetailsStyles from "./IncidentDetailsStyles";
import PhotoUpload from "./PhotoUpload";
// import CustomSelect, { SelectOption } from "../../components/common/Select";
import CustomSelect from "../../components/common/Select";

interface DamageTypeCardProps {
  type: string;
  selected: boolean;
  onClick: () => void;
  Icon: React.ElementType;
  title: string;
  subtitle: string;
  isFilled?: boolean;
}

const DamageTypeCard: React.FC<DamageTypeCardProps> = ({
  type,
  selected,
  onClick,
  Icon,
  title,
  subtitle,
  isFilled = false,
}) => (
  <Card
    sx={{
      ...IncidentDetailsStyles.card(selected),
      position: 'relative',
      cursor: 'pointer',
      '&:hover': {
        boxShadow: 3,
      },
    }}
    onClick={onClick}
    id={`incidentdetails_damagetype-${type}-card`}
    data-test-id={`test-incidentdetails_damagetype-${type}-card`}
    aria-label={`Select ${type} damage type`}
  >
    <CardContent sx={IncidentDetailsStyles.cardContent}>
      <Box sx={{ position: 'relative' }}>
        <Icon sx={IncidentDetailsStyles.icon} />
        {isFilled && (
          <CheckCircleIcon 
            sx={{ 
              position: 'absolute',
              top: -8,
              right: -8,
              color: '#4CAF50',
              fontSize: 24,
              backgroundColor: 'white',
              borderRadius: '50%',
              zIndex: 1
            }} 
          />
        )}
      </Box>
      <Typography variant="subtitle1" sx={IncidentDetailsStyles.title}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={IncidentDetailsStyles.subtitle}
      >
        {subtitle}
      </Typography>
    </CardContent>
  </Card>
);

interface InjuryTypeCardProps {
  type: "bodily-injury" | "property-damage";
  selected: boolean;
  onClick: () => void;
  Icon: React.ElementType;
  title: string;
  subtitle: string;
}

interface IncidentDetailsProps {
  formData: {
    dateOfLoss: string;
    timeOfLoss: string;
    reportedDate: string;
    reportedTime: string;
    incidentType: string;
    description: string;
    IncidentLocation: string;
    weatherConditions: string;
    policeReportFiled: boolean;
    policeReportNumber: string;
    authoritiesContacted: string;
    vehicleYear: string;
    vehicleMake: string;
    vehicleModel: string;
    vin: string;
    //licensePlate: string;
    vehicleLocation: string;
    vehicleDamageDescription: string;
    injuryType: "bodily-injury" | "property-damage" | null;
    injuryDescription: string;
    medicalProviderInfo: string;
    treatmentStatus: string;
    propertyType: string;
    propertyOwnerName: string;
    propertyOwnerContact: string;
    propertyDamageDescription: string;
    propertyDamagePhotos: File[];
    vehicleDamagePhotos: File[];
    hasWitness: boolean;
    witnessName: string;
    witnessPhone: string;
    witnessEmail: string;
    witnessRelationship: string;
    geoLocation: string;
    selectedVehicle: IPolicyVehicle | null;
    vehicleList?: IPolicyVehicle[];
  };
  selectedClaimType: "first-party" | "third-party" | null;
  selectedDamageType: "vehicle" | "injury" | "both" | null;
  onFormChange: (field: string, value: any) => void;
  onDamageTypeSelect: (type: "vehicle" | "injury" | "both") => void;
  policyVehicles?: IPolicyVehicle[];
  policyDetailsHeader?: IPolicyDetailsHeader | null;
  policyId?: string;
  lobCode?: string;
  onValidationChange?: (errors: { dateError: boolean; timeError: boolean }) => void;
}

const IncidentDetails: React.FC<IncidentDetailsProps> = ({
  formData,
  selectedClaimType,
  selectedDamageType,
  onFormChange,
  onDamageTypeSelect,
  policyVehicles,
  policyDetailsHeader,
  policyId,
  lobCode,
  onValidationChange,
}) => {
  const dispatch = useAppDispatch();
  const [locationError, setLocationError] = useState<string>("");
  const hasFetchedVehicles = useRef(false);
  const todayDateStr = new Date().toISOString().split("T")[0];
  const getNowTimeStr = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };
  const [dateOfLossInput, setDateOfLossInput] = useState<string>(formData.dateOfLoss || "");
  const [timeOfLossInput, setTimeOfLossInput] = useState<string>(formData.timeOfLoss || "");
  const [dateError, setDateError] = useState<string>("");
  const [timeError, setTimeError] = useState<string>("");

  useEffect(() => {
    setDateOfLossInput(formData.dateOfLoss || "");
  }, [formData.dateOfLoss]);

  useEffect(() => {
    setTimeOfLossInput(formData.timeOfLoss || "");
  }, [formData.timeOfLoss]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange({ dateError: Boolean(dateError), timeError: Boolean(timeError) });
    }
  }, [dateError, timeError, onValidationChange]);

  const carrier = getCookie("carrier");
  const system = getCookie("system");

  useEffect(() => {
    const fetchVehicleData = async () => {
      if (!policyId || !lobCode || !system || !carrier) {
        return;
      }

      if (!hasFetchedVehicles.current) {
        try {
          hasFetchedVehicles.current = true;
          const requestParams = {
            policyId,
            carrier,
            system,
            lobCode,
          };
          const response: any = await dispatch(GetPolicyRisks(requestParams));
          if (response?.payload?.data?.vehicleRiskList) {
            const vehicles = response.payload.data.vehicleRiskList;
            onFormChange("vehicleList", vehicles);
          }
        } catch (error) {
          console.error("Error fetching vehicle data:", error);
          hasFetchedVehicles.current = false;
        }
      }
    };

    fetchVehicleData();
  }, [policyId, lobCode, system, carrier, dispatch, onFormChange]);

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          //onFormChange("IncidentLocation",`${data.address.city},${data.address.city_district},${data.address.state},${data.address.postcode},${data.address.country}`);
          onFormChange(
            "geoLocation",
            `${data.address.city},${data.address.city_district},${data.address.state},${data.address.postcode},${data.address.country}`
          );
          setLocationError("");
        },
        (error) => {
          setLocationError(
            "Unable to retrieve your location. Please enter manually."
          );
          console.error("Error getting location:", error);
        }
      );
    } else {
      setLocationError("Geolocation is not supported by your browser.");
    }
  };
  const isFirstPartyClaim = selectedClaimType === "first-party";
  return (
    <Box sx={IncidentDetailsStyles.container}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontFamily: "var(--font-noto-sans)" }}
        id="incidentdetails_title"
        data-test-id="test-incidentdetails_title"
        aria-label="Incident Details Section"
      >
        Incident Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Date of Loss"
            type="date"
            value={dateOfLossInput}
            onChange={(e) => {
              const value = e.target.value;
              setDateOfLossInput(value);
              if (value && value > todayDateStr) {
                setDateError("Date of loss cannot be in the future.");
                return;
              }
              setDateError("");
              onFormChange("dateOfLoss", value);
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: todayDateStr }}
            error={Boolean(dateError)}
            helperText={dateError || undefined}
            id="incidentdetails_dateofloss-input"
            data-test-id="test-incidentdetails_dateofloss-input"
            aria-label="Enter date of loss"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Time of Loss"
            type="time"
            value={timeOfLossInput}
            onChange={(e) => {
              const value = e.target.value;
              const selectedDate = dateOfLossInput || formData.dateOfLoss;
              if (selectedDate && selectedDate > todayDateStr) {
                // Any time is invalid if date is in the future
                setTimeOfLossInput(value);
                setTimeError("Time of loss cannot be set for a future date.");
                return;
              }
              if (selectedDate === todayDateStr) {
                const nowStr = getNowTimeStr();
                if (value && value > nowStr) {
                  setTimeOfLossInput(value);
                  setTimeError("Time of loss cannot be in the future.");
                  return; // show error, do not update form state
                }
              }
              setTimeOfLossInput(value);
              setTimeError("");
              onFormChange("timeOfLoss", value);
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={() => {
              const selectedDate = dateOfLossInput || formData.dateOfLoss;
              if (selectedDate && selectedDate > todayDateStr) {
                return {}; // no max; we handle error explicitly for future date
              }
              return selectedDate === todayDateStr ? { max: getNowTimeStr() } : {};
            }}
            error={Boolean(timeError)}
            helperText={timeError || undefined}
            id="incidentdetails_timeofloss-input"
            data-test-id="test-incidentdetails_timeofloss-input"
            aria-label="Enter time of loss"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Reported Date"
            type="date"
            value={formData.reportedDate}
            onChange={(e) => onFormChange("reportedDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled
            id="incidentdetails_reporteddate-input"
            data-test-id="test-incidentdetails_reporteddate-input"
            aria-label="Reported date (auto-filled)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Reported Time"
            type="time"
            value={formData.reportedTime}
            onChange={(e) => onFormChange("reportedTime", e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled
            id="incidentdetails_reportedtime-input"
            data-test-id="test-incidentdetails_reportedtime-input"
            aria-label="Reported time (auto-filled)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomSelect
            label="Incident Type"
            options={[
              { value: "collision", label: "Collision" },
              { value: "theft", label: "Theft" },
              { value: "fire", label: "Fire" },
              { value: "weather", label: "Weather" },
            ]}
            value={formData.incidentType}
            onChange={(value) => onFormChange("incidentType", value)}
            required
            id="incidentdetails_incidenttype-select"
            data-test-id="test-incidentdetails_incidenttype-select"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Description"
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => onFormChange("description", e.target.value)}
            placeholder="Please describe what happened in detail"
            id="incidentdetails_description-input"
            data-test-id="test-incidentdetails_description-input"
            aria-label="Enter incident description"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="IncidentLocation"
            value={formData.IncidentLocation}
            onChange={(e) => onFormChange("IncidentLocation", e.target.value)}
            placeholder="Address, zip code, or coordinates"
            id="incidentdetails_location-input"
            data-test-id="test-incidentdetails_location-input"
            aria-label="Enter incident location"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Geo-tagged Location"
            value={formData.geoLocation || ""}
            onChange={(e) => onFormChange("geoLocation", e.target.value)}
            placeholder="Auto-fetched from user's device"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleGetLocation}
                  color="primary"
                  id="incidentdetails_getlocation-button"
                  data-test-id="test-incidentdetails_getlocation-button"
                  aria-label="Get current location"
                >
                  <LocationOnIcon />
                </IconButton>
              ),
            }}
            helperText={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {locationError && (
                  <Typography
                    variant="caption"
                    color="error"
                    id="incidentdetails_location-error"
                    data-test-id="test-incidentdetails_location-error"
                    aria-label="Location error message"
                  >
                    {locationError}
                  </Typography>
                )}
              </Box>
            }
            id="incidentdetails_geolocation-input"
            data-test-id="test-incidentdetails_geolocation-input"
            aria-label="Geo-tagged location"
          />
        </Grid>
        <Grid item xs={12}>
          <CustomSelect
            label="Weather Conditions"
            options={[
              { value: "clear", label: "Clear" },
              { value: "rain", label: "Rain" },
              { value: "snow", label: "Snow" },
              { value: "fog", label: "Fog" },
              { value: "wind", label: "Wind" },
            ]}
            value={formData.weatherConditions}
            onChange={(value) => onFormChange("weatherConditions", value)}
            id="incidentdetails_weather-select"
            data-test-id="test-incidentdetails_weather-select"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.policeReportFiled}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  onFormChange("policeReportFiled", isChecked);
                  if (!isChecked) {
                    onFormChange("policeReportNumber", "");
                    onFormChange("authoritiesContacted", "");
                  }
                }}
                id="incidentdetails_policereport-checkbox"
                data-test-id="test-incidentdetails_policereport-checkbox"
                aria-label="Police report was filed"
              />
            }
            label={
              <Typography
                sx={{ fontFamily: "var(--font-noto-sans)" }}
                id="incidentdetails_policereport-label"
                data-test-id="test-incidentdetails_policereport-label"
              >
                Police report was filed
              </Typography>
            }
          />
        </Grid>

        {formData.policeReportFiled && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Police Report Number"
                value={formData.policeReportNumber}
                onChange={(e) =>
                  onFormChange("policeReportNumber", e.target.value)
                }
                InputProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                InputLabelProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                id="incidentdetails_policereportnumber-input"
                data-test-id="test-incidentdetails_policereportnumber-input"
                aria-label="Enter police report number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Authorities Contacted"
                value={formData.authoritiesContacted}
                onChange={(e) =>
                  onFormChange("authoritiesContacted", e.target.value)
                }
                placeholder="Name of police department or authority"
                InputProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                InputLabelProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                id="incidentdetails_authorities-input"
                data-test-id="test-incidentdetails_authorities-input"
                aria-label="Enter authorities contacted"
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hasWitness || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  onFormChange("hasWitness", isChecked);
                  if (!isChecked) {
                    onFormChange("witnessName", "");
                    onFormChange("witnessPhone", "");
                    onFormChange("witnessEmail", "");
                    onFormChange("witnessRelationship", "");
                  }
                }}
                id="incidentdetails_witness-checkbox"
                data-test-id="test-incidentdetails_witness-checkbox"
                aria-label="Has witness information"
              />
            }
            label={
              <Typography
                sx={{ fontFamily: "var(--font-noto-sans)" }}
                id="incidentdetails_witness-label"
                data-test-id="test-incidentdetails_witness-label"
              >
                Witness Details
              </Typography>
            }
          />
        </Grid>

        {formData.hasWitness && (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Witness Name"
                value={formData.witnessName || ""}
                onChange={(e) => onFormChange("witnessName", e.target.value)}
                InputProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                InputLabelProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                id="incidentdetails_witnessname-input"
                data-test-id="test-incidentdetails_witnessname-input"
                aria-label="Enter witness name"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Witness Phone"
                value={formData.witnessPhone || ""}
                onChange={(e) => onFormChange("witnessPhone", e.target.value)}
                InputProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                InputLabelProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                id="incidentdetails_witnessphone-input"
                data-test-id="test-incidentdetails_witnessphone-input"
                aria-label="Enter witness phone number"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Witness Email"
                value={formData.witnessEmail || ""}
                onChange={(e) => onFormChange("witnessEmail", e.target.value)}
                InputProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                InputLabelProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                id="incidentdetails_witnessemail-input"
                data-test-id="test-incidentdetails_witnessemail-input"
                aria-label="Enter witness email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Relationship to Involved Parties"
                value={formData.witnessRelationship || ""}
                onChange={(e) =>
                  onFormChange("witnessRelationship", e.target.value)
                }
                placeholder="How does the witness know the involved parties?"
                InputProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                InputLabelProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                id="incidentdetails_witnessrelationship-input"
                data-test-id="test-incidentdetails_witnessrelationship-input"
                aria-label="Enter witness relationship to involved parties"
              />
            </Grid>
          </>
        )}

        {selectedClaimType === "third-party" ? (
          <>
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                gutterBottom
                id="incidentdetails_damagetype-title"
                data-test-id="test-incidentdetails_damagetype-title"
                aria-label="Type of Damage Section"
              >
                Type of Damage
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                id="incidentdetails_damagetype-description"
                data-test-id="test-incidentdetails_damagetype-description"
              >
                Please select the type of damage or injury you're claiming
              </Typography>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <DamageTypeCard
                    type="vehicle"
                    selected={selectedDamageType === "vehicle"}
                    onClick={() => onDamageTypeSelect("vehicle")}
                    Icon={DirectionsCarIcon}
                    title="Vehicle Damage"
                    subtitle="Damage to vehicle only"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DamageTypeCard
                    type="injury"
                    selected={selectedDamageType === "injury"}
                    onClick={() => onDamageTypeSelect("injury")}
                    Icon={HealingOutlinedIcon}
                    title="Injury/Property"
                    subtitle="Personal injury or property damage"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DamageTypeCard
                    type="both"
                    selected={selectedDamageType === "both"}
                    onClick={() => onDamageTypeSelect("both")}
                    Icon={LoopOutlinedIcon}
                    title="Both"
                    subtitle="Vehicle damage and injury/property damage"
                  />
                </Grid>
              </Grid>
            </Grid>

            {(selectedDamageType === "vehicle" ||
              selectedDamageType === "both") && (
              <>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={IncidentDetailsStyles.vehicleTitle}
                    id="incidentdetails_vehicleinfo-title"
                    data-test-id="test-incidentdetails_vehicleinfo-title"
                    aria-label="Vehicle Information Section"
                  >
                    Vehicle Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    type="number"
                    fullWidth
                    required
                    label="Year"
                    value={formData.vehicleYear}
                    onChange={(e) =>
                      onFormChange("vehicleYear", e.target.value)
                    }
                    placeholder="Vehicle year"
                    id="incidentdetails_vehicleyear-input"
                    data-test-id="test-incidentdetails_vehicleyear-input"
                    aria-label="Enter vehicle year"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="Make"
                    value={formData.vehicleMake}
                    onChange={(e) =>
                      onFormChange("vehicleMake", e.target.value)
                    }
                    placeholder="Vehicle make"
                    id="incidentdetails_vehiclemake-input"
                    data-test-id="test-incidentdetails_vehiclemake-input"
                    aria-label="Enter vehicle make"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="Model"
                    value={formData.vehicleModel}
                    onChange={(e) =>
                      onFormChange("vehicleModel", e.target.value)
                    }
                    placeholder="Vehicle model"
                    id="incidentdetails_vehiclemodel-input"
                    data-test-id="test-incidentdetails_vehiclemodel-input"
                    aria-label="Enter vehicle model"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="VIN"
                    value={formData.vin}
                    onChange={(e) => onFormChange("vin", e.target.value)}
                    placeholder="Vehicle identification number"
                    InputProps={{
                      style: { fontFamily: "var(--font-noto-sans)" },
                    }}
                    InputLabelProps={{
                      style: { fontFamily: "var(--font-noto-sans)" },
                    }}
                    id="incidentdetails_vin-input"
                    data-test-id="test-incidentdetails_vin-input"
                    aria-label="Enter vehicle identification number"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <CustomSelect
                    label="Vehicle Location"
                    options={[
                      { value: "drivable", label: "Drivable" },
                      { value: "towed", label: "Towed" },
                      { value: "garage", label: "Garage" },
                    ]}
                    value={formData.vehicleLocation}
                    onChange={(value) => onFormChange("vehicleLocation", value)}
                    id="incidentdetails_vehiclelocation-select"
                    data-test-id="test-incidentdetails_vehiclelocation-select"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    label="Vehicle Damage Description"
                    value={formData.vehicleDamageDescription}
                    onChange={(e) =>
                      onFormChange("vehicleDamageDescription", e.target.value)
                    }
                    placeholder="Describe the damage to the vehicle"
                    id="incidentdetails_vehicledamage-input"
                    data-test-id="test-incidentdetails_vehicledamage-input"
                    aria-label="Enter vehicle damage description"
                  />
                </Grid>
                <Grid item xs={12}>
                  <PhotoUpload
                    photos={formData.vehicleDamagePhotos}
                    onPhotosChange={(photos) =>
                      onFormChange("vehicleDamagePhotos", photos)
                    }
                    title="Upload Vehicle Damage Photos"
                    type="vehicle"
                  />
                </Grid>
              </>
            )}
          </>
        ) : null}

        {selectedClaimType === "first-party" && (
          <>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={IncidentDetailsStyles.vehicleTitle}
              >
                Vehicle Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel sx={{ fontFamily: "var(--font-noto-sans)" }}>
                  Select Vehicle
                </InputLabel>
                <Select
                  value={formData.selectedVehicle?.vin || ""}
                  onChange={(e) => {
                    const selectedVin = e.target.value;
                    const vehicle = (
                      formData.vehicleList || policyVehicles
                    )?.find((v) => v.vin === selectedVin);
                    onFormChange("selectedVehicle", vehicle);
                    if (vehicle) {
                      onFormChange("vin", vehicle.vin);
                      onFormChange("vehicleMake", vehicle.make);
                      onFormChange("vehicleModel", vehicle.model);
                      onFormChange("vehicleYear", vehicle.manufactureYr);
                    } else {
                      onFormChange("vin", "");
                      onFormChange("vehicleMake", "");
                      onFormChange("vehicleModel", "");
                      onFormChange("vehicleYear", "");
                    }
                  }}
                  sx={{ fontFamily: "var(--font-noto-sans)" }}
                  label="Select Vehicle"
                >
                  <MenuItem
                    value=""
                    sx={{ fontFamily: "var(--font-noto-sans)" }}
                  >
                    Select a vehicle
                  </MenuItem>
                  {(formData.vehicleList || policyVehicles)?.map((vehicle) => (
                    <MenuItem
                      key={vehicle.vin}
                      value={vehicle.vin}
                      sx={{ fontFamily: "var(--font-noto-sans)" }}
                    >
                      {`${vehicle.manufactureYr} ${vehicle.make} ${vehicle.model} (${vehicle.vin})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="VIN"
                value={formData.vin}
                onChange={(e) => onFormChange("vin", e.target.value)}
                placeholder="Vehicle identification number"
                InputProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                InputLabelProps={{
                  style: { fontFamily: "var(--font-noto-sans)" },
                }}
                id="incidentdetails_vin-input"
                data-test-id="test-incidentdetails_vin-input"
                aria-label="Enter vehicle identification number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomSelect
                label="Vehicle Location"
                required
                options={[
                  { value: "drivable", label: "Drivable" },
                  { value: "towed", label: "Towed" },
                  { value: "garage", label: "Garage" },
                ]}
                value={formData.vehicleLocation}
                onChange={(value) => onFormChange("vehicleLocation", value)}
                id="incidentdetails_vehiclelocation-select"
                data-test-id="test-incidentdetails_vehiclelocation-select"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Vehicle Damage Description"
                value={formData.vehicleDamageDescription}
                onChange={(e) =>
                  onFormChange("vehicleDamageDescription", e.target.value)
                }
                placeholder="Describe the damage to the vehicle"
                id="incidentdetails_vehicledamage-input"
                data-test-id="test-incidentdetails_vehicledamage-input"
                aria-label="Enter vehicle damage description"
              />
            </Grid>
            <Grid item xs={12}>
              <PhotoUpload
                photos={formData.vehicleDamagePhotos}
                onPhotosChange={(photos) =>
                  onFormChange("vehicleDamagePhotos", photos)
                }
                title="Upload Vehicle Damage Photos"
                type="vehicle"
              />
            </Grid>

            <Divider sx={{ 
              my: 3,
              borderWidth: '1px',
              borderColor: 'rgba(0, 0, 0, 0.12)',
              width: '100%'
            }} />
                
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                sx={IncidentDetailsStyles.vehicleTitle}
              >
                Injury Info
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Injury Description"
                value={formData.injuryDescription}
                onChange={(e) =>
                  onFormChange("injuryDescription", e.target.value)
                }
                placeholder="Describe the injuries sustained"
                id="incidentdetails_injurydescription-input"
                data-test-id="test-incidentdetails_injurydescription-input"
                aria-label="Enter injury description"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Medical Provider Information"
                value={formData.medicalProviderInfo}
                onChange={(e) =>
                  onFormChange("medicalProviderInfo", e.target.value)
                }
                placeholder="Name and contact information of medical provider (optional)"
                id="incidentdetails_medicalprovider-input"
                data-test-id="test-incidentdetails_medicalprovider-input"
                aria-label="Enter medical provider information"
              />
            </Grid>
            <Grid item xs={12}>
              <CustomSelect
                label="Treatment Status"
                options={[
                  { value: "treated", label: "Treated" },
                  { value: "ongoing", label: "Ongoing" },
                  { value: "not-treated", label: "Not Treated" },
                ]}
                value={formData.treatmentStatus}
                onChange={(value) => onFormChange("treatmentStatus", value)}
                id="incidentdetails_treatmentstatus-select"
                data-test-id="test-incidentdetails_treatmentstatus-select"
              />
            </Grid>
          </>
        )}

        {(selectedDamageType === "injury" || selectedDamageType === "both") &&
          selectedClaimType === "third-party" && (
            <>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={IncidentDetailsStyles.injuryTitle}
                  id="incidentdetails_injuryinfo-title"
                  data-test-id="test-incidentdetails_injuryinfo-title"
                  aria-label="Injury/Property Damage Information Section"
                >
                  Injury/Property Damage Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  id="incidentdetails_injurytype-title"
                  data-test-id="test-incidentdetails_injurytype-title"
                  aria-label="Type of Damage Selection"
                >
                  Type of Damage
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DamageTypeCard
                      type="bodily-injury"
                      selected={formData.injuryType === "bodily-injury"}
                      onClick={() =>
                        onFormChange("injuryType", "bodily-injury")
                      }
                      Icon={AccessibilityOutlinedIcon}
                      title="Bodily Injury"
                      subtitle="Physical injury to person"
                      isFilled={Boolean(formData.injuryDescription && formData.treatmentStatus)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DamageTypeCard
                      type="property-damage"
                      selected={formData.injuryType === "property-damage"}
                      onClick={() =>
                        onFormChange("injuryType", "property-damage")
                      }
                      Icon={HomeWorkOutlinedIcon}
                      title="Property Damage"
                      subtitle="Damage to property other than vehicle"
                      isFilled={Boolean(formData.propertyType && formData.propertyDamageDescription)}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {formData.injuryType === "property-damage" && (
                <>
                  <Grid item xs={12}>
                    <CustomSelect
                      label="Property Type"
                      options={[
                        { value: "vehicle", label: "Vehicle" },
                        { value: "building", label: "Building" },
                        { value: "fence", label: "Fence" },
                        { value: "other", label: "Other" },
                      ]}
                      value={formData.propertyType || ""}
                      onChange={(value) => onFormChange("propertyType", value)}
                      required
                      id="incidentdetails_propertytype-select"
                      data-test-id="test-incidentdetails_propertytype-select"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={4}
                      label="Property Damage Description"
                      value={formData.propertyDamageDescription}
                      onChange={(e) =>
                        onFormChange(
                          "propertyDamageDescription",
                          e.target.value
                        )
                      }
                      placeholder="Describe the damage to the property"
                      id="incidentdetails_propertydamage-input"
                      data-test-id="test-incidentdetails_propertydamage-input"
                      aria-label="Enter property damage description"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Property Owner Name"
                      value={formData.propertyOwnerName}
                      onChange={(e) =>
                        onFormChange("propertyOwnerName", e.target.value)
                      }
                      placeholder="Enter property owner's name (optional)"
                      id="incidentdetails_propertyownername-input"
                      data-test-id="test-incidentdetails_propertyownername-input"
                      aria-label="Enter property owner name"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Property Owner Contact"
                      value={formData.propertyOwnerContact}
                      onChange={(e) =>
                        onFormChange("propertyOwnerContact", e.target.value)
                      }
                      placeholder="Enter property owner's contact (optional)"
                      id="incidentdetails_propertyownercontact-input"
                      data-test-id="test-incidentdetails_propertyownercontact-input"
                      aria-label="Enter property owner contact"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PhotoUpload
                      photos={formData.propertyDamagePhotos}
                      onPhotosChange={(photos) =>
                        onFormChange("propertyDamagePhotos", photos)
                      }
                      title="Upload Property Damage Photos"
                      type="property"
                    />
                  </Grid>
                </>
              )}

              {formData.injuryType === "bodily-injury" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required={selectedClaimType === "third-party"}
                      multiline
                      rows={4}
                      label="Injury Description"
                      value={formData.injuryDescription}
                      onChange={(e) =>
                        onFormChange("injuryDescription", e.target.value)
                      }
                      placeholder="Describe the injuries sustained"
                      id="incidentdetails_injurydescription-input"
                      data-test-id="test-incidentdetails_injurydescription-input"
                      aria-label="Enter injury description"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Medical Provider Information"
                      value={formData.medicalProviderInfo}
                      onChange={(e) =>
                        onFormChange("medicalProviderInfo", e.target.value)
                      }
                      placeholder="Name and contact information of medical provider (optional)"
                      id="incidentdetails_medicalprovider-input"
                      data-test-id="test-incidentdetails_medicalprovider-input"
                      aria-label="Enter medical provider information"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelect
                      label="Treatment Status"
                      options={[
                        { value: "treated", label: "Treated" },
                        { value: "ongoing", label: "Ongoing" },
                        { value: "not-treated", label: "Not Treated" },
                      ]}
                      value={formData.treatmentStatus}
                      onChange={(value) =>
                        onFormChange("treatmentStatus", value)
                      }
                      id="incidentdetails_treatmentstatus-select"
                      data-test-id="test-incidentdetails_treatmentstatus-select"
                    />
                  </Grid>
                </>
              )}
            </>
          )}
      </Grid>
    </Box>
  );
};

export default IncidentDetails;
